// Shared source of truth for the "pending actions" counts surfaced both in the
// main navigation and the notification bell (#199).
//
// COUNT_SOURCES is the single declaration of the three count lists: which store
// module + list name holds each count, the constrain used to fetch it, the gate
// that decides whether it's visible, and the bell's label/route. Both this
// module (getters + refresh) and NavigationC (list init) consume it, so the list
// names / constrains live in exactly one place.
//
// Note on gating: the bell's category visibility is `feature-flag (+ role)` — the
// canonical "is this actionable for me" test. It is intentionally simpler than
// NavigationC's per-placement inline `v-if`s (which additionally vary by
// treasury/view-mode/submit-permission for where a count is shown in the nav).
// Both read the same underlying `list.<name>.total`, so the numbers never drift;
// only where each surface chooses to show them differs.

import { invoiceRequestLabels } from '@/lib/invoiceRequestLabels'
import { transferMode } from '@/lib/costCenterTransfers'
import { orgTerms } from '@/terminology'

const showMemberApplication = import.meta.env.VITE_SHOW_MEMBER_APPLICATION

export const COUNT_SOURCES = [
  {
    key: 'memberApplications',
    module: 'memberApplications',
    listName: 'menuIcon',
    constrain: null,
    // Candidaturas — admins only. NavigationC only fetches this list for
    // admin/editor, so gate on the role (not just the stale number).
    adminOnly: true,
    settingFlag: 'hasMemberApplication',
    requiresMemberApplicationEnv: true,
    route: 'memberApplicationsList',
    singular: 'candidatura',
    plural: 'candidaturas',
  },
  {
    key: 'expenseSubmissions',
    module: 'expenseSubmissions',
    listName: 'attentionCount',
    // The backend scopes the attention count by role (admin pending vs member
    // attention); the gate is just the feature flag.
    constrain: { attention: 'true' },
    settingFlag: 'hasExpenseSubmissions',
    route: 'expenseSubmissionList',
    singular: 'despesa',
    plural: 'despesas',
  },
  {
    key: 'invoiceRequests',
    module: 'invoiceRequests',
    listName: 'pendingCount',
    constrain: { pending: 'true' },
    settingFlag: 'hasInvoiceRequests',
    route: 'invoiceRequestList',
    singular: invoiceRequestLabels.lower,
    plural: invoiceRequestLabels.lowerPlural,
  },
  {
    key: 'kmMaps',
    module: 'kmMaps',
    listName: 'pendingCount',
    // Backend `_pending` = status IN ('submitted','info_requested','question'),
    // documented as "awaiting an admin decision" (kmMaps/controller.go). The
    // matching notification (km_maps_pending) is admin-only, so this is the
    // admin approval queue — gate on the role, not just the number. generateStore
    // prefixes the key with `_`, so { pending: 'true' } → &_pending=true.
    constrain: { pending: 'true' },
    adminOnly: true,
    settingFlag: 'hasKmMaps',
    route: 'kmMapList',
    singular: 'mapa de deslocação',
    plural: 'mapas de deslocação',
  },
  {
    // #514 — cost-centre transfers waiting for treasury. The setting has three
    // values rather than a true/false flag, so the gate is `isOn`: any mode but
    // off (pending ones stay decidable after a switch to automatic). Treasury's
    // queue, so admin/editor only — a member's own pending transfers are not
    // something they have to act on.
    key: 'costCenterTransfers',
    module: 'costCenterTransfers',
    listName: 'pendingCount',
    constrain: { status: 'submitted' },
    adminOnly: true,
    isOn: (settingsByName) => transferMode(settingsByName) !== 'off',
    route: 'costCenterTransferList',
    singular: 'transferência',
    plural: 'transferências',
  },
  {
    // #501 — members whose answers wait for an admin to apply them. Open
    // requests still waiting on the member are not the admin's to act on, so
    // only `answered` counts. Zero when the org has no admin review.
    key: 'memberVerifications',
    module: 'members',
    listName: 'verificationAnswered',
    constrain: { verification: 'answered' },
    adminOnly: true,
    settingFlag: 'hasMemberVerification',
    // Off by default everywhere: without `isOn` every refresh would still query
    // /members for a count the getter hides.
    isOn: (settingsByName) => settingsByName.hasMemberVerification?.value === 'true',
    route: 'membersList',
    routeQuery: { verificacao: 'answered' },
    // Functions: orgTerms arrives from the API after this module is built.
    singular: () => `${orgTerms.memberSingular?.toLowerCase() ?? 'membro'} com dados por aprovar`,
    plural: () => `${orgTerms.memberPlural?.toLowerCase() ?? 'membros'} com dados por aprovar`,
  },
]

export default (_app) => {
  return {
    namespaced: true,
    getters: {
      // The actionable categories visible for the current role/view, each with
      // its live total and the list route it deep-links to.
      categories(state, getters, rootState, rootGetters) {
        const isAdminOrEditor = rootGetters['auth/isAdminOrEditor']
        const settingsByName = rootGetters['settings/listByName']
        const settingOn = (name) => settingsByName[name]?.value === 'true'

        return COUNT_SOURCES.filter((s) => {
          if (s.adminOnly && !isAdminOrEditor) return false
          if (s.requiresMemberApplicationEnv && showMemberApplication === 'false') return false
          if (s.settingFlag && !settingOn(s.settingFlag)) return false
          if (s.isOn && !s.isOn(settingsByName)) return false
          return true
        }).map((s) => ({
          key: s.key,
          singular: typeof s.singular === 'function' ? s.singular() : s.singular,
          plural: typeof s.plural === 'function' ? s.plural() : s.plural,
          total: rootState[s.module]?.list[s.listName]?.total || 0,
          routeName: s.route,
          routeQuery: s.routeQuery,
        }))
      },
      // Non-zero categories — what the bell dropdown lists.
      pendingCategories(state, getters) {
        return getters.categories.filter((c) => c.total > 0)
      },
      // Single badge total = sum of the visible actionable counts.
      badgeTotal(state, getters) {
        return getters.categories.reduce((sum, c) => sum + (c.total || 0), 0)
      },
    },
    actions: {
      // The one refetch path: NavigationC, the bell, and the ws:counts:refresh
      // handler all call this. It is self-contained — it `init`s each list first
      // (idempotent: createList only runs if the list is missing), so it is safe
      // to dispatch even before NavigationC has created the lists. Candidaturas
      // are only fetched for admins, mirroring the getter gate.
      refresh({ dispatch, rootGetters }) {
        if (!rootGetters['auth/isLoggedin']) return
        const isAdminOrEditor = rootGetters['auth/isAdminOrEditor']
        const settingsByName = rootGetters['settings/listByName']

        COUNT_SOURCES.forEach((s) => dispatch(`${s.module}/init`, s.listName, { root: true }))

        for (const s of COUNT_SOURCES) {
          if (s.adminOnly && !isAdminOrEditor) continue
          // A source behind `isOn` is not fetched while its feature is off: with
          // transfers off (every organisation by default) the bell must not add a
          // request — and a 404 — to every refresh (#514 review).
          if (s.isOn && !s.isOn(settingsByName)) continue
          if (s.constrain) {
            dispatch(
              `${s.module}/setConstrain`,
              { listName: s.listName, ...s.constrain },
              { root: true }
            )
          }
          dispatch(`${s.module}/list`, { listName: s.listName }, { root: true })
        }
      },
    },
  }
}
